import CheckCircle from '@mui/icons-material/CheckCircle'
import {
  Alert,
  Button,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { useGetProjectModel } from '@/service-library/hooks/project-models'
import {
  useGetPubModels,
  useUpdatePubModel,
} from '@/service-library/hooks/pub-models'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { prettifyDate } from '@/utils/time'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import PublishNewVersionDialog from './PublishNewVersionDialog'

export default function PublishedModelManagementView() {
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const projectModelId = selectedWorkflow?.project_model_id
  const publishDialogOverlay = useOverlay()

  const { projectModel, isPending: isLoadingProjectModel } = useGetProjectModel(
    {
      id: projectModelId || '',
      filters: {
        fields__include: 'project_model_versions',
        project_model_versions__fields__include: 'training_started_at',
      },
      enabled: !!projectModelId,
    },
  )

  const {
    pubModels,
    isPending: isLoadingPubModels,
    queryKey,
  } = useGetPubModels({
    filters: {
      limit: '1',
      project_model_id: projectModelId || undefined,
      fields__include: 'pub_model_versions',
    },
    enabled: !!projectModelId,
  })

  const { updatePubModel } = useUpdatePubModel({
    listQueryKey: queryKey,
    onSuccess: () => {
      showSuccessSnackbar('Recommended Version Updated')
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to Update Recommended Version. Please try again.',
      )
    },
  })

  const pubModel = pubModels[0]
  const pubModelVersions = pubModel?.pub_model_versions || []

  const handleSetRecommended = (versionId: string) => {
    if (!pubModel) return
    updatePubModel({
      ...pubModel,
      recommended_version_id: versionId,
    })
  }

  // Create a map of project model versions for easy lookup
  const projectModelVersionsMap = new Map(
    projectModel?.project_model_versions?.map((v) => [v.id, v]) || [],
  )

  // Get pub model versions with their corresponding project model version data
  const enrichedPubModelVersions = pubModelVersions
    .map((pubVersion) => {
      const projectVersion = projectModelVersionsMap.get(
        pubVersion.model_version_id,
      )
      return {
        ...pubVersion,
        project_version: projectVersion,
        is_recommended: pubVersion.id === pubModel?.recommended_version_id,
      }
    })
    .sort((a, b) => {
      // Sort by version number descending (newest first)
      const aVer = a.version[0] * 100 + (a.version[1] || 0)
      const bVer = b.version[0] * 100 + (b.version[1] || 0)
      return bVer - aVer
    })

  const isLoading = isLoadingProjectModel || isLoadingPubModels

  // A disabled query stays pending, so check for a model id before loading.
  const hasNoModel = !projectModelId || (!isLoading && !pubModel)

  const content = hasNoModel ? (
    <Alert severity="info">
      No project model selected. Select a workflow that has a project model.
    </Alert>
  ) : isLoading ? (
    <Skeleton variant="rounded" height={240} />
  ) : enrichedPubModelVersions.length === 0 ? (
    <Alert severity="info">
      No versions have been published yet for this model.
    </Alert>
  ) : (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Published version</TableCell>
              <TableCell>Internal version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Training date</TableCell>
              <TableCell>Document count</TableCell>
              <TableCell>Accuracy</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enrichedPubModelVersions.map((version) => (
              <TableRow key={version.id}>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={version.is_recommended ? 'primary' : 'inherit'}
                  >
                    v{version.version[0]}.{version.version[1] || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {version.project_version?.version
                      ? `v${version.project_version.version}${
                          String(version.project_version.version).includes('.')
                            ? ''
                            : '.0'
                        }`
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      version.project_version?.training_status || 'Unknown'
                    }
                    size="small"
                    color={
                      version.project_version?.training_status === 'completed'
                        ? 'success'
                        : 'default'
                    }
                  />
                </TableCell>
                <TableCell>
                  {version.project_version?.training_started_at
                    ? prettifyDate(version.project_version.training_started_at)
                    : '—'}
                </TableCell>
                <TableCell>
                  {version.project_version?.document_count ?? '—'}
                </TableCell>
                <TableCell>
                  {version.project_version?.accuracy
                    ? `${(
                        parseFloat(version.project_version.accuracy) * 100
                      ).toFixed(1)}%`
                    : '—'}
                </TableCell>
                <TableCell>
                  {version.is_recommended ? (
                    <Chip
                      icon={<CheckCircle />}
                      label="Recommended"
                      size="small"
                      color="primary"
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleSetRecommended(version.id)}
                    >
                      Set as recommended
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )

  return (
    <>
      <ProjectBreadcrumb label="Published Model" url="../published-model" />

      <SettingsPage
        title="Published Model Management"
        description={
          pubModel
            ? `The published versions of ${pubModel.name}, and which one is recommended.`
            : undefined
        }
        width="lg"
        actions={
          pubModel && (
            <>
              <CopyIDButton stringToCopy={pubModel.id} />
              <Button
                variant="text"
                onClick={publishDialogOverlay.open}
                disabled={!projectModel?.project_model_versions}
              >
                Publish new version
              </Button>
            </>
          )
        }
      >
        {content}
      </SettingsPage>

      {pubModel && projectModel?.project_model_versions && (
        <PublishNewVersionDialog
          overlay={publishDialogOverlay}
          pubModelId={pubModel.id}
          projectModelVersions={projectModel.project_model_versions}
          existingPubModelVersions={pubModelVersions}
          pubModelsQueryKey={queryKey}
        />
      )}
    </>
  )
}
